/**
 * TaskFormDialog - Modal de criação/edição de tarefa
 * 
 * Suporta criação atômica: cria tarefa via RPC e depois envia
 * arquivos staged (pendentes) sequencialmente. Se a RPC falhar,
 * nenhum upload acontece.
 * 
 * @version 1.13.0
 * RELEASE: 2026-10-04 v1.13.0 - Correção: descrição da tarefa anterior reaparecia ao
 *          criar nova tarefa. Limpa descrição/anexos pendentes ao fechar e remonta o
 *          editor a cada abertura em modo criação (key por sessão).
 * RELEASE: 2026-08-28 v1.12.0 - O bloco "Veículos" só é renderizado quando o módulo
 *   Veículos está habilitado no tenant (flag `vehicles_enabled`).

 * RELEASE: 2026-08-21 v1.11.0 - Seletor "Tipo da tarefa" na criação de subtarefa
 *   (prop `allowTypeSelection`). O componente exportado passa a ser um wrapper
 *   que envolve o formulário em <TaskModuleProvider>, permitindo criar uma
 *   subtarefa de tipo diferente do da tarefa pai (ex.: Normal dentro de Jurídico).
 *   Ao trocar o tipo, categoria/fase/classificação são limpas e recarregadas.
 * RELEASE: 2026-08-21 v1.10.0 - Botões "+" nos campos Cliente/Contato, Negócio e
 *   Veículo. Abrem os MESMOS modais de criação usados nas páginas
 *   (CreateContactDialog / DealFormDialog / VehicleFormDialog) e selecionam
 *   automaticamente o registro criado. Só aparecem com permissão de criação
 *   (contacts / crm_deals / vehicles_list; admin tem bypass).
 * RELEASE: 2026-08-21 v1.9.0 - O seletor de veículos passa a ser escopado pelo
 *   cliente/contato selecionado (lista apenas os veículos daquele cliente).
 *   Sem cliente selecionado a busca segue livre em toda a base do tenant.
 * RELEASE: 2026-08-18 v1.7.0 - Corrige deslocamento de fuso no campo "Prazo".
 *   A leitura do due_date (ISO em UTC) passa a ser convertida para o horário
 *   LOCAL antes de alimentar o input datetime-local, evitando que cada edição
 *   somasse o offset do fuso (+3h) ao prazo já salvo.
 * RELEASE: 2026-08-10 v1.6.0 - Nova prop `defaultVehicles` (lista) para herdar
 *   múltiplos veículos ao criar subtarefa a partir da tarefa pai.
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-06 v1.1.0 - Suporte a criação atômica com staged files
 * RELEASE: 2026-08-08 v1.3.0 - Novo campo opcional "Veículo" (vínculo com
 *   customer_vehicles) na criação e edição da tarefa.
 * RELEASE: 2026-07-09 v1.2.0 - Novo campo opcional "Template de Checklist"
 *   na criação. Reutiliza useTaskChecklistTemplatesList + useApplyTaskChecklistTemplate
 *   (mode=append). Não afeta edição nem criação sem template selecionado.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, Plus, ClipboardList, Link2, Layers, GitBranch, Tag, Flag, Calendar, User, Briefcase, Car, ListTree, ListChecks, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CurrencyInput } from '@/components/ui/currency-input';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { DealSelector } from '@/components/tasks/DealSelector';
import { VehicleSelector } from '@/components/tasks/VehicleSelector';
import { useCustomerVehicles, useTaskVehicleLink } from '@/hooks/useCustomerVehicles';
import { ParentTaskSelector } from '@/components/tasks/ParentTaskSelector';
// v1.10.0 — modais de criação inline reaproveitados das páginas
import { CreateContactDialog } from '@/components/tenant/contacts/CreateContactDialog';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { VehicleFormDialog } from '@/components/vehicles/VehicleFormDialog';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useVehiclesModuleEnabled, useVehiclesModule } from '@/hooks/useVehiclesModule';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskModule, TASK_MODULES, type TaskModuleType } from '@/hooks/useTaskModuleType';
// v1.13.0 — tipos de tarefa permitidos ao usuário (flags do tenant + permissões)
import { useAvailableTaskTypes } from '@/hooks/useAvailableTaskTypes';

// v1.11.0 — permite forçar o tipo do formulário (subtarefa de outro módulo)
import { TaskModuleProvider } from '@/contexts/TaskModuleContext';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useTaskClassifications } from '@/hooks/useTaskClassifications';
import { useTaskCreationOptions } from '@/hooks/useTaskCreationOptions';
import { useTaskEditData } from '@/hooks/useTaskEditData';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import {
  useTaskChecklistTemplatesList,
  useApplyTaskChecklistTemplate,
} from '@/hooks/useTaskChecklistTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TaskDescriptionAndAttachments } from '@/components/tasks/TaskDescriptionAndAttachments';
import { uploadTaskAttachmentDirect } from '@/hooks/useTaskAttachments';
import { sanitizeHtml } from '@/lib/sanitize';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { ClassificationSelect } from '@/components/tasks/ClassificationSelect';

// Logger para depuração
const logger = createLogger('TaskFormDialog');

/**
 * Tipos de ação disponíveis para tarefas do módulo Jurídico.
 * Lista fixa para padronizar o cadastro (substitui o antigo campo livre).
 */
const LEGAL_ACTION_TYPES = [
  'Civil',
  'Trabalhista',
  'Criminal',
  'Tributário',
  'Administrativo',
  'Previdenciário',
] as const;

/**
 * Converte um timestamp ISO (normalmente em UTC, vindo do banco) para o
 * formato aceito pelo input `datetime-local` (`YYYY-MM-DDTHH:mm`), usando os
 * componentes de data/hora LOCAIS do navegador.
 *
 * IMPORTANTE (v1.7.0): NUNCA cortar a string ISO (ex.: `iso.slice(0, 16)`),
 * pois isso exibe o horário UTC como se fosse local e, ao salvar
 * (`new Date(valor).toISOString()`), o prazo era deslocado pelo offset do
 * fuso a cada edição — erro cumulativo.
 */
const isoToDatetimeLocal = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID da tarefa para edição (null = criação) */
  editTaskId?: string | null;
  /** Categoria pré-selecionada (para criação) */
  defaultCategoryId?: string;
  /** Fase pré-selecionada (para criação) */
  defaultPhaseId?: string;
  /** ID da tarefa pai pré-selecionada (para criação de subtarefa) */
  defaultParentTaskId?: string;
  /** v1.2.0: Negócio pré-selecionado (uso a partir do modal de Negócio) */
  defaultDealId?: string;
  /** v1.2.0: Contato pré-selecionado (uso a partir do modal de Cliente) */
  defaultContactId?: string;
  /** v1.4.0: Veículo pré-selecionado (uso a partir do modal de Veículo) */
  defaultVehicleId?: string;
  /** v1.5.0: Rótulo do veículo pré-selecionado (exibido no chip) */
  defaultVehicleLabel?: string;
  /**
   * v1.6.0: Lista de veículos pré-selecionados (herança da tarefa pai ao criar
   * subtarefa). Quando informada, tem precedência sobre `defaultVehicleId`.
   */
  defaultVehicles?: Array<{ id: string; label: string }>;
  /** Callback de sucesso: recebe taskId e taskNumber para o modal de sucesso */
  onSuccess?: (taskId?: string, taskNumber?: number) => void;
  /** v1.2.0: Disparado quando o trigger do banco bloqueia por limite mensal de tarefas. */
  onLimitReached?: () => void;
  /**
   * v1.11.0: Exibe o seletor "Tipo da tarefa" (Normal | Retenção | Jurídico).
   * Usado apenas na CRIAÇÃO de subtarefas — permite que a subtarefa seja de um
   * tipo diferente do da tarefa pai.
   */
  allowTypeSelection?: boolean;
}

export type { TaskFormDialogProps };

/** Props internas: o wrapper injeta o controle do seletor de tipo */
interface TaskFormDialogInnerProps extends TaskFormDialogProps {
  /** Controle do seletor de tipo (undefined = seletor oculto) */
  typeSelector?: {
    value: TaskModuleType;
    onChange: (value: TaskModuleType) => void;
    /** v1.13.0 — tipos permitidos ao usuário (flags do tenant + permissões) */
    options: TaskModuleType[];
  };
}


function TaskFormDialogInner({
  open,
  onOpenChange,
  editTaskId,
  defaultCategoryId,
  defaultPhaseId,
  defaultParentTaskId,
  defaultDealId,
  defaultContactId,
  defaultVehicleId,
  defaultVehicleLabel,
  defaultVehicles,
  onSuccess,
  onLimitReached,
  typeSelector,
}: TaskFormDialogInnerProps) {
  const { currentTenant, user } = useAuth();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskModule = useTaskModule();
  const queryClient = useQueryClient();
  const isEditing = !!editTaskId;

  // Estados do formulário
  const [title, setTitle] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [classificationId, setClassificationId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState('');
  // Responsável pela tarefa
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  // Relacionamentos
  const [contactId, setContactId] = useState<string | null>(null);
  /**
   * v1.9.0 — cliente (crm_customers) derivado do contato selecionado.
   * Usado apenas para escopar o seletor de veículos.
   */
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);
  // v1.3.0: Veículos vinculados (opcional). v1.4.0: múltiplos veículos por tarefa.
  const [vehicles, setVehicles] = useState<Array<{ id: string; label: string }>>([]);
  const [parentTaskId, setParentTaskId] = useState<string | null>(null);
  /** Prazo da tarefa pai — limita o prazo da subtarefa */
  const [parentDueDate, setParentDueDate] = useState<string | null>(null);

  /**
   * v1.9.0 — resolve o cliente vinculado ao contato quando o contato veio de
   * outra origem (edição da tarefa ou auto-preenchimento pelo negócio).
   * Reaproveita a RPC search_contacts_for_selector (server-side, sem SELECT direto).
   */
  const { data: resolvedCustomerId } = useQuery({
    queryKey: ['task-form-contact-customer', contactId],
    enabled: !!contactId && !customerId,
    staleTime: 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.rpc('search_contacts_for_selector', {
        p_contact_id: contactId!,
      });
      if (error || !data || data.length === 0) return null;
      return (data[0] as { customer_id: string | null }).customer_id ?? null;
    },
  });

  /** Escopo efetivo do seletor de veículos (cliente explícito > derivado) */
  const vehicleScopeCustomerId = customerId ?? resolvedCustomerId ?? null;
  /**
   * v1.11.0 — a restrição da listagem ao cliente/contato passa a depender da
   * configuração do tenant "Restringir seleção aos veículos do cliente".
   * Desligada (padrão), a busca continua global (ex.: locadoras).
   */
  const { scopeToCustomer: vehiclesScopeToCustomer } = useVehiclesModule();

  // =====================================================================
  // v1.10.0 — Criação inline (botões "+") dos relacionamentos
  // =====================================================================
  const { canCreate: canCreateMenu, isAdmin: isTenantAdmin } = usePermissionsContext();
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();

  /** Permissões de criação de cada entidade relacionada (admin com bypass) */
  const canCreateContact = isTenantAdmin || canCreateMenu('contacts');
  const canCreateDeal = isTenantAdmin || canCreateMenu('crm_deals');
  const canCreateVehicle =
    vehiclesModuleEnabled && (isTenantAdmin || canCreateMenu('vehicles_list'));

  /** Estado de abertura de cada modal de criação inline */
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);
  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);



  // v1.2.0: Template de Checklist opcional (aplicado após create_task no modo criação)
  const [checklistTemplateId, setChecklistTemplateId] = useState<string | null>(null);

  /**
   * v1.8.0: Campos específicos dos módulos Retenção e Jurídico. São gravados
   * nas tabelas satélite (task_retention_details / task_legal_details) pelas
   * RPCs create_task/update_task através do parâmetro p_module_data.
   */
  const [moduleData, setModuleData] = useState<Record<string, string>>({});
  /** Helper para atualizar um campo do módulo mantendo os demais */
  const setModuleField = (field: string, value: string) =>
    setModuleData((prev) => ({ ...prev, [field]: value }));

  /**
   * v1.11.0 — Troca de tipo pelo seletor (criação de subtarefa).
   * Categoria, fase e classificação pertencem a um tipo específico, portanto
   * são limpas quando o usuário muda o tipo. Os campos específicos do módulo
   * (moduleData) também são descartados. Relacionamentos (cliente, negócio,
   * veículo, responsável, tarefa pai) são preservados de propósito.
   */
  const previousModuleTypeRef = useRef<TaskModuleType>(taskModule.type);
  useEffect(() => {
    if (!typeSelector) return;
    if (previousModuleTypeRef.current === taskModule.type) return;
    previousModuleTypeRef.current = taskModule.type;
    setCategoryId('');
    setPhaseId('');
    setClassificationId('');
    setModuleData({});
  }, [taskModule.type, typeSelector]);


  // Arquivos pendentes (staged) para upload após criação
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Estado de progresso para upload de anexos
  const [uploadProgress, setUploadProgress] = useState('');

  // Flag para controlar se já preencheu o formulário com dados da RPC
  const [editDataLoaded, setEditDataLoaded] = useState(false);
  // Flag para garantir que o reset do modo criação rode UMA ÚNICA vez por
  // abertura do modal. Sem isso, alterações em `categories` (causadas por
  // refetch de useTaskCreationOptions ao trocar o Responsável) reexecutavam
  // o effect e apagavam título/descrição/classificação/prazo/etc.
  const [createDataInitialized, setCreateDataInitialized] = useState(false);
  // v1.13.0: chave da sessão de criação (usada como `key` do bloco de descrição)
  const [createSessionKey, setCreateSessionKey] = useState(0);

  // Dados auxiliares — modo edição usa RPC server-side, modo criação usa hooks
  const { data: editData, isLoading: isLoadingEditData } = useTaskEditData(
    open && isEditing ? editTaskId : null
  );

  // v1.4.0: veículos já vinculados à tarefa (múltiplos) + mutation de vínculo
  const { vehicles: linkedVehicles } = useCustomerVehicles(
    { taskId: open && isEditing ? editTaskId : null },
    open && isEditing,
  );
  const linkVehicleMutation = useTaskVehicleLink();

  /** Sincroniza os chips de veículos com os vínculos existentes (modo edição) */
  useEffect(() => {
    if (open && isEditing) {
      setVehicles(
        linkedVehicles.map((v) => ({
          id: v.id,
          label: [v.plate, v.brand_name, v.model_name].filter(Boolean).join(' · ') || 'Veículo',
        })),
      );
    }
  }, [open, isEditing, linkedVehicles]);

  // Detecta se o usuário trocou de categoria no modo edição
  const categoryChanged = isEditing && editDataLoaded && !!categoryId && categoryId !== editData?.task?.category_id;

  // v1.3.0 (2026-06-09): habilitamos `useTaskCreationOptions` também em edição
  // para aplicar o mesmo filtro server-side por permissão granular quando o
  // usuário troca a categoria (destino tratado como criação — can_create).
  // Em edição na categoria original, get_task_edit_data v1.1.0 já filtra por
  // can_edit no servidor e garante categoria/fase atual da tarefa na lista.
  const creationOptions = useTaskCreationOptions(open, assignedTo);

  // v1.2.0: lista de templates ativos (usada só no modo criação — hook seguro
  // em edição pois apenas cacheia; UI não renderiza o campo)
  const {
    data: checklistTemplates,
    isLoading: isLoadingChecklistTemplates,
  } = useTaskChecklistTemplatesList(true);
  const applyChecklistTemplateMutation = useApplyTaskChecklistTemplate();

  // v1.2.0 (2026-06-09): permissões granulares por fase para campo Responsável.
  // Em edição, se o perfil do usuário não tem `can_change_responsible` na fase
  // atual, o seletor deve ficar desabilitado (server-side update_task já valida).
  const { canDoOnPhase: canDoOnTaskPhase } = useTaskPhasePermissions(categoryId);
  const canChangeResponsible = !isEditing
    ? true
    : canDoOnTaskPhase(phaseId, 'can_change_responsible');
  const { categories: createCategoriesLegacy } = useTaskCategories(false);
  void createCategoriesLegacy;
  const { phases: hookPhases } = useTaskPhases(undefined);
  void hookPhases;
  // v1.10.0: sempre consulta as classificações da categoria corrente — a RPC já
  // devolve também as classificações GLOBAIS do módulo (category_id NULL), usadas
  // em Retenção/Jurídico como "motivo" e que não dependem da categoria.
  const { classifications: hookClassifications } = useTaskClassifications(
    categoryId || undefined
  );

  // Fontes de dados unificadas
  const categories = isEditing ? (editData?.categories || []) : creationOptions.categories;
  const phases = isEditing
    ? (categoryChanged ? creationOptions.getPhases(categoryId) : (editData?.phases || []))
    : creationOptions.getPhases(categoryId);
  // Mapeia classificações da RPC para o formato esperado pelo ClassificationSelect
  const classifications = useMemo(() => {
    if (!isEditing || categoryChanged) return hookClassifications;

    // Modo edição sem troca de categoria: lista vinda de get_task_edit_data
    const fromEdit = (editData?.classifications || []).map(c => ({
      ...c,
      category_id: editData?.task?.category_id || '',
      category_name: '',
      description: null as string | null,
      sort_order: 0,
      is_active: true,
      created_at: '',
      updated_at: '',
      tenant_id: '',
      parent_name: c.parent_name || null,
      children_count: 0,
      tasks_count: 0,
    }));

    // v1.10.0: mescla as globais do módulo (não retornadas por get_task_edit_data)
    const ids = new Set(fromEdit.map(c => c.id));
    const globals = hookClassifications.filter(c => !c.category_id && !ids.has(c.id));
    return [...fromEdit, ...globals] as typeof hookClassifications;
  }, [isEditing, categoryChanged, editData, hookClassifications]);


  // Callback estável para evitar re-renders desnecessários
  const handlePendingFilesChange = useCallback((files: File[]) => {
    setPendingFiles(files);
  }, []);

  // Preenche formulário com dados da RPC (modo edição)
  useEffect(() => {
    if (open && isEditing && editData?.task && !editDataLoaded) {
      const t = editData.task;
      setTitle(t.title);
      setDescriptionHtml(t.description_html || t.description || '');
      setCategoryId(t.category_id);
      setPhaseId(t.phase_id);
      setClassificationId(t.classification_id || '');
      setPriority(t.priority);
      // v1.7.0: converte o ISO (UTC) para horário local do navegador
      setDueDate(isoToDatetimeLocal(t.due_date));
      setAssignedTo(t.assigned_to || null);
      setContactId(t.contact_id || null);
      setDealId(t.deal_id || null);
      // v1.4.0: os veículos vinculados vêm da RPC list_customer_vehicles (efeito abaixo)
      setParentTaskId(t.parent_task_id || null);
      setParentDueDate(t.parent_due_date || null);
      // v1.8.0: pré-carrega os campos do módulo (Retenção/Jurídico)
      const details: any = (t as any).retention_details || (t as any).legal_details || null;
      setModuleData(
        details
          ? Object.fromEntries(
              Object.entries(details).map(([k, v]) => [k, v == null ? '' : String(v)]),
            )
          : {},
      );
      setPendingFiles([]);
      setUploadProgress('');
      setEditDataLoaded(true);
    }
  }, [open, isEditing, editData, editDataLoaded]);

  // v1.3.0: pré-seleciona o usuário logado como responsável assim que o modal
  // abre em modo criação, independente de categorias terem carregado.
  // Necessário para perfis cuja permissão exige assigned_to = auth.uid()
  // e para evitar que o campo apareça vazio (gerando aviso de permissão).
  useEffect(() => {
    if (open && !isEditing && user?.id) {
      setAssignedTo((prev) => prev ?? user.id);
    }
  }, [open, isEditing, user?.id]);

  // Reseta formulário ao abrir no modo criação (apenas UMA vez por abertura)
  // Aguarda `categories` carregar antes de inicializar, para conseguir
  // pré-selecionar a primeira categoria quando não há defaultCategoryId.
  useEffect(() => {
    if (open && !isEditing && !createDataInitialized && categories.length > 0) {
      setTitle('');
      setDescriptionHtml('');
      setCategoryId(defaultCategoryId || categories[0]?.id || '');
      setPhaseId(defaultPhaseId || '');
      setClassificationId('');
      setPriority('normal');
      setDueDate('');
      // v1.2.0: pré-seleciona o próprio usuário como responsável. Isso é
      // necessário porque perfis com permissão apenas em "Usuário Responsável"
      // só conseguem criar tarefa quando assigned_to = auth.uid().
      setAssignedTo(user?.id ?? null);
      // v1.2.0: pré-popula contato/negócio quando criado a partir dos modais CRM
      setContactId(defaultContactId || null);
      setDealId(defaultDealId || null);
      // v1.4.0: veículo pré-selecionado quando criado a partir do modal de Veículo
      // v1.6.0: lista completa (herdada da tarefa pai) tem precedência
      setVehicles(
        defaultVehicles?.length
          ? defaultVehicles.map((v) => ({ id: v.id, label: v.label }))
          : defaultVehicleId
            ? [{ id: defaultVehicleId, label: defaultVehicleLabel || 'Veículo selecionado' }]
            : [],
      );
      setParentTaskId(defaultParentTaskId || null);
      setParentDueDate(null);
      setPendingFiles([]);
      setUploadProgress('');
      setChecklistTemplateId(null);
      setCreateDataInitialized(true);
    }
  }, [open, isEditing, createDataInitialized, defaultCategoryId, defaultPhaseId, defaultParentTaskId, defaultDealId, defaultContactId, defaultVehicleId, defaultVehicleLabel, defaultVehicles, categories, user?.id]);

  // Reseta flag de carregamento ao fechar modal
  useEffect(() => {
    if (!open) {
      setEditDataLoaded(false);
      setCreateDataInitialized(false);
      // v1.13.0: em criação, limpa descrição/anexos pendentes já no fechamento,
      // para o editor nascer vazio na próxima abertura (evita texto da tarefa anterior)
      if (!isEditing) {
        setDescriptionHtml('');
        setPendingFiles([]);
      }
    } else if (!isEditing) {
      // v1.13.0: nova sessão de criação → remonta o editor de descrição do zero
      setCreateSessionKey((k) => k + 1);
    }
  }, [open, isEditing]);

  // Auto-seleciona primeira fase ao mudar categoria (criação ou edição com troca)
  useEffect(() => {
    if (phases.length > 0 && !phaseId) {
      setPhaseId(phases[0].id);
    }
  }, [phases, phaseId]);

  // Reseta fase e classificação ao trocar categoria no modo edição
  useEffect(() => {
    if (categoryChanged) {
      // Fase atual não pertence à nova categoria — resetar
      const phaseInCategory = phases.some(p => p.id === phaseId);
      if (!phaseInCategory) {
        setPhaseId(phases.length > 0 ? phases[0].id : '');
      }
      // Classificação atual não pertence à nova categoria — resetar
      const clsInCategory = classifications.some(c => c.id === classificationId);
      if (!clsInCategory) {
        setClassificationId('');
      }
    }
  }, [categoryChanged, phases, classifications, phaseId, classificationId]);

  // v1.9.0 (Retenção/Jurídico): flags de módulo e travas de categoria/fase padrão
  /** true quando o modal está em contexto de Retenção ou Jurídico */
  const isModuleTask = taskModule.type !== 'normal';
  /** true apenas no módulo Retenção */
  const isRetention = taskModule.type === 'retention';
  /**
   * Categoria/Fase padrão do módulo (opcional). Quando configuradas, os campos
   * são pré-selecionados e travados na CRIAÇÃO, evitando abrir solicitação na
   * categoria errada. Quando não há padrão, tudo permanece editável (como hoje).
   */
  const hasDefaultCategory = !isEditing && isModuleTask && !!creationOptions.defaultCategoryId;
  const hasDefaultPhase = hasDefaultCategory && !!creationOptions.defaultPhaseId;

  /** Aplica a categoria/fase padrão na criação de solicitações de módulo */
  useEffect(() => {
    if (isEditing || !isModuleTask) return;
    if (creationOptions.defaultCategoryId && categoryId !== creationOptions.defaultCategoryId) {
      setCategoryId(creationOptions.defaultCategoryId);
      setPhaseId(creationOptions.defaultPhaseId || '');
      return;
    }
    if (creationOptions.defaultPhaseId && !phaseId) {
      setPhaseId(creationOptions.defaultPhaseId);
    }
  }, [isEditing, isModuleTask, creationOptions.defaultCategoryId, creationOptions.defaultPhaseId, categoryId, phaseId]);

  // v1.2.0: na CRIAÇÃO, ao mudar o responsável a lista de fases criáveis pode
  // mudar (permissões "Usuário Responsável"). Se a categoria/fase atual deixar
  // de existir nas opções permitidas, resetar para evitar envio inválido.
  useEffect(() => {
    if (isEditing || creationOptions.isLoading) return;
    if (categoryId && !creationOptions.categories.some(c => c.id === categoryId)) {
      setCategoryId('');
      setPhaseId('');
      setClassificationId('');
      return;
    }
    if (categoryId && phaseId) {
      const allowed = creationOptions.getPhases(categoryId);
      if (!allowed.some(p => p.id === phaseId)) {
        setPhaseId('');
      }
    }
  }, [isEditing, creationOptions.isLoading, creationOptions.categories, creationOptions.getPhases, categoryId, phaseId]);

  // Mutation para criar tarefa (com upload atômico de staged files)
  const createMutation = useMutation({
    mutationFn: async () => {
      const sanitizedHtml = sanitizeHtml(descriptionHtml);
      const plainText = sanitizedHtml.replace(/<[^>]*>/g, '').trim();

      // Passo 1: Criar tarefa via RPC (retorna [{ id, number }])
      const { data: rpcResult, error } = await supabase.rpc('create_task', {
        // v1.9.0: em Retenção/Jurídico o título é opcional — a RPC usa o
        // protocolo gerado automaticamente quando p_title vier nulo.
        p_title: title.trim() || null,
        p_category_id: categoryId,
        p_phase_id: phaseId,
        p_description: plainText || null,
        p_description_html: sanitizedHtml || null,
        p_classification_id: classificationId || null,
        p_priority: priority as any,
        p_due_date: dueDate ? new Date(dueDate).toISOString() : null,
        p_assigned_to: assignedTo || null,
        p_contact_id: contactId || null,
        p_deal_id: dealId || null,
        p_vehicle_id: vehicles[0]?.id || null,
        p_parent_task_id: parentTaskId || null,
        // v1.8.0: campos próprios de Retenção/Jurídico (ignorado quando normal)
        p_module_data: taskModule.type === 'normal' ? null : (moduleData as any),
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) throw error;
      // v1.5.1: RPC devolve TABLE(task_id, task_number) → primeiro item do array
      const taskRow: any = Array.isArray(rpcResult) ? rpcResult[0] : rpcResult;
      const taskId = taskRow?.task_id ?? taskRow?.id;
      if (!taskId) throw new Error('ID da tarefa não retornado');

      // v1.4.0: vincula os demais veículos selecionados. O primeiro vai em
      // p_vehicle_id e, desde create_task v1.12.0, a própria RPC grava o
      // vínculo N:N em task_vehicles (fonte lida pela aba "Veículos").
      for (const vehicle of vehicles.slice(1)) {
        try {
          await linkVehicleMutation.mutateAsync({ taskId: taskId as string, vehicleId: vehicle.id });
        } catch (linkError: any) {
          logger.warn('Falha ao vincular veículo à tarefa:', linkError.message);
        }
      }


      // Passo 2: Upload sequencial dos arquivos pendentes (staged)
      if (pendingFiles.length > 0 && currentTenant?.id) {
        for (let i = 0; i < pendingFiles.length; i++) {
          const file = pendingFiles[i];
          setUploadProgress(`Enviando anexo ${i + 1}/${pendingFiles.length}...`);
          try {
            await uploadTaskAttachmentDirect(taskId as string, currentTenant.id, file);
          } catch (uploadError: any) {
            // Aviso individual — tarefa já foi criada, continua com os outros
            logger.warn(`Falha ao enviar anexo ${file.name}:`, uploadError.message);
            toast.error(`Falha ao enviar: ${file.name}`);
          }
        }
      }

      // Passo 3 (v1.2.0): aplica template de checklist opcional em modo append.
      // Se falhar, apenas avisa — a tarefa já foi criada com sucesso.
      if (checklistTemplateId) {
        setUploadProgress('Aplicando template de checklist...');
        try {
          await applyChecklistTemplateMutation.mutateAsync({
            taskId: taskId as string,
            templateId: checklistTemplateId,
            mode: 'append',
          });
        } catch (e: any) {
          logger.warn('Falha ao aplicar template de checklist', { error: e?.message });
          // O hook já dispara toast de erro — não duplicar aqui.
        }
      }

      return taskId;

    },
    onSuccess: async (taskId) => {
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['task-attachments'] });
      setUploadProgress('');
      onOpenChange(false);

      // Buscar número da tarefa criada para exibir no modal de sucesso
      let taskNumber: number | undefined;
      try {
        const { data: taskData } = await supabase
          .from('tasks')
          .select('number')
          .eq('id', taskId as string)
          .single();
        taskNumber = taskData?.number ?? undefined;
      } catch {
        // Se falhar ao buscar número, continua sem ele
      }

      onSuccess?.(taskId as string, taskNumber);
    },
    onError: (err: Error) => {
      setUploadProgress('');
      // Detecta o erro específico do trigger de limite mensal de tarefas e
      // delega para o callback do componente pai (que abre o LimitReachedModal).
      // Mensagem definida pelo trigger `trg_enforce_tasks_monthly_limit`.
      const isTasksLimit =
        err.message?.includes('tasks_monthly_limit_reached') ||
        err.message?.toLowerCase().includes('limite mensal de tarefas');
      if (isTasksLimit && onLimitReached) {
        onOpenChange(false);
        onLimitReached();
        return;
      }
      toast.error(`Erro ao criar tarefa: ${err.message}`);
    },
  });

  // Mutation para atualizar tarefa
  const updateMutation = useMutation({
    mutationFn: async () => {
      const sanitizedHtml2 = sanitizeHtml(descriptionHtml);
      const plainText2 = sanitizedHtml2.replace(/<[^>]*>/g, '').trim();
      const { error } = await supabase.rpc('update_task', {
        p_id: editTaskId!,
        p_title: title.trim() || null,
        p_description: plainText2 || null,
        p_description_html: sanitizedHtml2 || null,
        p_category_id: categoryId,
        p_phase_id: phaseId,
        p_classification_id: classificationId || null,
        p_priority: priority as any,
        p_due_date: dueDate ? new Date(dueDate).toISOString() : null,
        p_assigned_to: assignedTo || null,
        p_contact_id: contactId || null,
        p_deal_id: dealId || null,
        p_parent_task_id: parentTaskId || null,
        p_clear_contact: !contactId && !!(editData?.task?.contact_id),
        p_clear_deal: !dealId && !!(editData?.task?.deal_id),
        p_clear_parent_task: !parentTaskId && !!(editData?.task?.parent_task_id),
        // v1.8.0: campos próprios de Retenção/Jurídico (ignorado quando normal)
        p_module_data: taskModule.type === 'normal' ? null : (moduleData as any),
        p_tenant_hint: currentTenant?.id || null,
      });
      if (error) throw error;

      // v1.4.0: sincroniza vínculos N:N de veículos (link_task_vehicle mantém
      // a coluna legada tasks.vehicle_id atualizada automaticamente)
      const currentIds = linkedVehicles.map((v) => v.id);
      const selectedIds = vehicles.map((v) => v.id);
      const toLink = selectedIds.filter((id) => !currentIds.includes(id));
      const toUnlink = currentIds.filter((id) => !selectedIds.includes(id));
      for (const vehicleId of toLink) {
        await linkVehicleMutation.mutateAsync({ taskId: editTaskId!, vehicleId });
      }
      for (const vehicleId of toUnlink) {
        await linkVehicleMutation.mutateAsync({ taskId: editTaskId!, vehicleId, unlink: true });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-details', editTaskId] });
      queryClient.invalidateQueries({ queryKey: ['task-edit-data', editTaskId] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
      toast.success('Tarefa atualizada com sucesso');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: Error) => toast.error(`Erro ao atualizar tarefa: ${err.message}`),
  });

  const handleSubmit = () => {
    // Validação: campos obrigatórios. v1.9.0 — em Retenção/Jurídico o título
    // não é exigido (o protocolo automático assume o papel de identificador).
    if ((!isModuleTask && !title.trim()) || !categoryId || !phaseId || !classificationId || !priority || !assignedTo) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Validação: prazo da subtarefa não pode exceder prazo da tarefa pai
    if (parentDueDate && dueDate) {
      const parentLimit = new Date(parentDueDate).getTime();
      const childDue = new Date(dueDate).getTime();
      if (childDue > parentLimit) {
        toast.error('O prazo da subtarefa não pode exceder o prazo da tarefa pai');
        return;
      }
    }

    // v1.8.0: número do processo é obrigatório no módulo Jurídico
    if (taskModule.type === 'legal' && !(moduleData.process_number || '').trim()) {
      toast.error('Informe o número do processo');
      return;
    }

    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Texto dinâmico do botão de submit
  const getSubmitLabel = () => {
    if (isEditing) return 'Salvar';
    if (uploadProgress) return uploadProgress;
    if (createMutation.isPending) return 'Criando tarefa...';
    return 'Criar Tarefa';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Empilhamento automático via DialogDepthContext (dialog.tsx v2.0.0) */}
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header premium — ícone em box + título + descrição contextual */}
        <DialogHeader className="flex flex-row items-center gap-3 px-6 py-4 border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 ring-1 ring-primary/20 shrink-0">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div className="flex flex-col text-left">
            <DialogTitle className="text-base font-semibold leading-tight">
              {isEditing ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              {isEditing
                ? `Atualize os dados da tarefa #${editData?.task?.number || ''}`
                : 'Preencha os campos para criar uma nova tarefa'}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Corpo scrollável com ScrollArea */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">

            {/* ═══════════════════════════════════════════ */}
            {/* v1.13.0 — Tipo da tarefa                    */}
            {/* Exibido na criação de subtarefa e nos       */}
            {/* atalhos rápidos (header/chat), somente com  */}
            {/* os tipos permitidos ao usuário.             */}
            {/* ═══════════════════════════════════════════ */}
            {typeSelector && (
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Tipo da tarefa *
                </Label>
                <Select
                  value={typeSelector.value}
                  onValueChange={(v) => typeSelector.onChange(v as TaskModuleType)}
                >
                  {/* v1.12.0 — largura compacta (proporcional aos rótulos) */}
                  <SelectTrigger className="h-9 border-border/50 w-full sm:w-[260px]">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeSelector.options.map((t) => (
                      <SelectItem key={t} value={t}>
                        {TASK_MODULES[t].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {parentTaskId
                    ? 'A subtarefa será criada no módulo escolhido e aparecerá na listagem dele, mantendo o vínculo com a tarefa pai.'
                    : 'O registro será criado no módulo escolhido e aparecerá na listagem dele. Categoria, fase e classificação mudam conforme o tipo.'}
                </p>
              </div>
            )}





            {/* ═══════════════════════════════════════════ */}
            {/* v1.8.0 — Seção específica do módulo         */}
            {/* Exibida apenas em Retenção e Jurídico.      */}
            {/* ═══════════════════════════════════════════ */}
            {taskModule.type !== 'normal' && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-primary" />
                  Dados de {taskModule.label}
                </h3>

                {/* v1.9.0: Nº do protocolo — gerado automaticamente pela RPC
                    create_task. Sempre somente leitura. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Nº do protocolo</Label>
                    <Input
                      readOnly
                      value={(editData?.task as any)?.protocol_number || ''}
                      placeholder="Gerado automaticamente ao salvar"
                      className="h-9 border-border/50 bg-muted text-muted-foreground font-mono cursor-default"
                    />
                  </div>
                  {isRetention && (
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Data da solicitação</Label>
                      <Input
                        type="date"
                        value={(moduleData.requested_at || '').slice(0, 10)}
                        onChange={(e) => setModuleField('requested_at', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>
                  )}
                  {/* Jurídico: data de entrada — base do cálculo do "Período" */}
                  {!isRetention && (
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Data de entrada</Label>
                      <Input
                        type="date"
                        value={(moduleData.entry_date || '').slice(0, 10)}
                        onChange={(e) => setModuleField('entry_date', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>
                  )}
                </div>

                {isRetention ? null : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Linha 1: número do processo + advogado responsável */}
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Número do processo *</Label>
                      <Input
                        maxLength={60}
                        placeholder="0000000-00.0000.0.00.0000"
                        value={moduleData.process_number || ''}
                        onChange={(e) => setModuleField('process_number', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Advogado responsável</Label>
                      <Input
                        maxLength={150}
                        placeholder="Nome do advogado"
                        value={moduleData.lawyer || ''}
                        onChange={(e) => setModuleField('lawyer', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>

                    {/* Linha 2: tipo de ação (lista fixa) + tribunal/vara */}
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Tipo de ação</Label>
                      <Select
                        value={moduleData.action_type || ''}
                        onValueChange={(v) => setModuleField('action_type', v)}
                      >
                        <SelectTrigger className="h-9 border-border/50">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {LEGAL_ACTION_TYPES.map((tipo) => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Tribunal / Vara</Label>
                      <Input
                        maxLength={150}
                        placeholder="Ex.: 3ª Vara Cível"
                        value={moduleData.court || ''}
                        onChange={(e) => setModuleField('court', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>

                    {/* Linha 3: valores financeiros do processo (máscara de moeda) */}
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Valor da causa (R$)</Label>
                      <CurrencyInput
                        value={Number(moduleData.case_value) || 0}
                        onChange={(v) => setModuleField('case_value', v ? String(v) : '')}
                        className="h-9 border-border/50"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Valor original da dívida (R$)</Label>
                      <CurrencyInput
                        value={Number(moduleData.original_debt_value) || 0}
                        onChange={(v) => setModuleField('original_debt_value', v ? String(v) : '')}
                        className="h-9 border-border/50"
                      />
                    </div>


                    {/* Linha 4: prazo processual com ajuda contextual */}
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Prazo processual
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="O que é o prazo processual?" className="text-muted-foreground hover:text-foreground">
                                <HelpCircle className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[260px] text-xs">
                              Data limite definida pelo tribunal para a próxima manifestação
                              ou providência no processo. É apenas informativa: serve de
                              referência para a equipe jurídica e não altera o prazo da tarefa.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Input
                        type="date"
                        value={moduleData.deadline_date || ''}
                        onChange={(e) => setModuleField('deadline_date', e.target.value)}
                        className="h-9 border-border/50"
                      />
                    </div>
                  </div>
                )}

              </div>
            )}



            {/* ═══════════════════════════════════════════ */}
            {/* Seção: Informações Básicas                 */}
            {/* ═══════════════════════════════════════════ */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-primary" />
                Informações Básicas
              </h3>

              <div className="space-y-4">
                {/* Número da tarefa (somente modo edição) + Título.
                    v1.9.0: em Retenção/Jurídico o Título é ocultado — a
                    identificação passa a ser o Nº do protocolo. */}
                {isModuleTask ? null : isEditing && editData?.task?.number ? (
                  <div className="grid grid-cols-[140px_1fr] gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                        <span className="text-muted-foreground/60">#</span>
                        Número
                      </Label>
                      <Input
                        value={`#${editData.task.number}`}
                        readOnly
                        className="h-9 border-border/50 bg-muted text-muted-foreground font-mono cursor-default"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                        <ClipboardList className="w-3 h-3" />
                        Título *
                      </Label>
                      <Input
                        placeholder="Título da tarefa..."
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        autoFocus
                        className="h-11 text-base border-border/50"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <ClipboardList className="w-3 h-3" />
                      Título *
                    </Label>
                    <Input
                      placeholder="Título da tarefa..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      autoFocus
                      className="h-11 text-base border-border/50"
                    />
                  </div>
                )}

                {/* v1.2.0 (2026-06-02) / banner v1.1.0 (2026-06-03):
                    Diferencia "tenant sem categorias/fases cadastradas" de "sem permissão". */}
                {!isEditing && !creationOptions.isLoading && !creationOptions.hasAnyCreatablePhase && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                    {!creationOptions.tenantHasPhases
                      ? (creationOptions.isAdmin
                          ? 'Nenhuma categoria ou fase de tarefa cadastrada neste tenant. Acesse Configurações → Tarefas para criar antes de prosseguir.'
                          : 'Ainda não há categorias ou fases de tarefa configuradas neste tenant. Solicite ao administrador a configuração inicial.')
                      : 'Você não tem permissão para criar tarefas em nenhuma fase. Solicite ao administrador a configuração em Permissões por Fase.'}
                  </div>
                )}

                {/* Grid 2 colunas: Categoria + Fase */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Layers className="w-3 h-3" />
                      Categoria *
                    </Label>
                    {/* v1.9.0: travado quando existe categoria padrão do módulo */}
                    <Select
                      value={categoryId}
                      onValueChange={(v) => { setCategoryId(v); setPhaseId(''); }}
                      disabled={hasDefaultCategory}
                    >
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasDefaultCategory && (
                      <p className="text-[11px] text-muted-foreground">
                        Este campo está desabilitado durante a criação da solicitação.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <GitBranch className="w-3 h-3" />
                      Fase *
                    </Label>
                    <Select value={phaseId} onValueChange={setPhaseId} disabled={hasDefaultPhase}>
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {phases.map(phase => (
                          <SelectItem key={phase.id} value={phase.id}>
                            <div className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: phase.color }} />
                              {phase.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasDefaultPhase && (
                      <p className="text-[11px] text-muted-foreground">
                        Este campo está desabilitado durante a criação da solicitação.
                      </p>
                    )}
                  </div>
                </div>

                {/* Grid 2 colunas: Classificação + Prioridade */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Tag className="w-3 h-3" />
                      Classificação *
                    </Label>
                    <ClassificationSelect
                      value={classificationId}
                      onChange={setClassificationId}
                      classifications={classifications}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Flag className="w-3 h-3" />
                      Prioridade *
                    </Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['low', 'normal', 'high', 'urgent'] as const).map((p) => (
                          <SelectItem key={p} value={p}>
                            <span className="flex items-center gap-2">
                              <PriorityFlag priority={p} size={14} />
                              {PRIORITY_LABELS[p]}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Grid 2 colunas: Prazo + Responsável.
                    v1.9.0: em Retenção o Prazo é ocultado (a data relevante é
                    a Data da solicitação). */}
                <div className="grid grid-cols-2 gap-3">
                  {!isRetention && (
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      Prazo
                    </Label>
                    <Input
                      type="datetime-local"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      max={isoToDatetimeLocal(parentDueDate) || undefined}
                      className="h-9 border-border/50"
                    />
                    {/* Hint visual: limite de prazo da tarefa pai */}
                    {parentDueDate && (
                      <p className="text-xs text-muted-foreground">
                        Limite: {new Date(parentDueDate).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      Responsável *
                    </Label>
                    <SystemUserSelector
                      value={assignedTo}
                      onSelect={(userId) => setAssignedTo(userId)}
                      placeholder="Selecionar responsável..."
                      disabled={!canChangeResponsible}
                    />
                    {isEditing && !canChangeResponsible && (
                      <p className="text-xs text-muted-foreground">
                        Você não tem permissão para alterar o responsável nesta fase.
                      </p>
                    )}
                  </div>
                </div>

                {/* v1.2.0: Template de Checklist (opcional, apenas na criação).
                    Para tarefas já criadas, o botão "Aplicar template" continua
                    disponível no modal de detalhes via ChecklistApplyTemplateDialog. */}
                {!isEditing && (
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <ListChecks className="w-3 h-3" />
                      Template de Checklist
                      <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">
                        (opcional)
                      </span>
                    </Label>
                    <Select
                      value={checklistTemplateId ?? '__none__'}
                      onValueChange={(v) =>
                        setChecklistTemplateId(v === '__none__' ? null : v)
                      }
                      disabled={isLoadingChecklistTemplates}
                    >
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue
                          placeholder={
                            isLoadingChecklistTemplates
                              ? 'Carregando...'
                              : 'Nenhum template'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          <span className="text-muted-foreground">Nenhum template</span>
                        </SelectItem>
                        {(checklistTemplates ?? []).map((tpl) => (
                          <SelectItem key={tpl.id} value={tpl.id}>
                            <span className="flex flex-col">
                              <span className="text-sm">{tpl.name}</span>
                              <span className="text-[11px] text-muted-foreground">
                                {tpl.group_count} listas · {tpl.item_count} itens
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isLoadingChecklistTemplates &&
                      (checklistTemplates?.length ?? 0) === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Nenhum template disponível. Cadastre em Configurações → Templates de Checklist.
                        </p>
                      )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Descrição rica + Anexos */}
            <TaskDescriptionAndAttachments
              /* v1.13.0: key por sessão de criação força editor vazio a cada nova abertura */
              key={isEditing ? `edit-${editTaskId}` : `create-${createSessionKey}`}
              taskId={editTaskId || undefined}
              descriptionHtml={descriptionHtml}
              onDescriptionChange={setDescriptionHtml}
              onPendingFilesChange={handlePendingFilesChange}
            />

            <Separator />

            {/* ═══════════════════════════════════════════ */}
            {/* Seção: Relacionamentos                     */}
            {/* ═══════════════════════════════════════════ */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-primary" />
                Relacionamentos
              </h3>

              <div className="space-y-4">
                {/* Grid 2 colunas: Cliente + Negócio */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      Cliente / Contato
                    </Label>
                    {/* v1.10.0 — seletor + atalho de criação inline */}
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <ContactSelector
                          value={contactId}
                          onSelect={setContactId}
                          onSelectContact={(contact) => {
                            // v1.9.0: guarda o cliente do contato para escopar os veículos
                            setCustomerId(contact?.customer_id ?? null);
                            // Troca de cliente invalida os veículos já escolhidos
                            if (contact?.id !== contactId) setVehicles([]);
                          }}
                          placeholder="Selecionar contato..."
                        />
                      </div>
                      {canCreateContact && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon-sm"
                          title="Novo contato"
                          aria-label="Novo contato"
                          onClick={() => setCreateContactOpen(true)}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Briefcase className="w-3 h-3" />
                      Negócio
                    </Label>
                    {/* v1.10.0 — seletor + atalho de criação inline */}
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <DealSelector
                          value={dealId}
                          onSelect={setDealId}
                          contactId={contactId}
                          onDealData={(deal) => {
                            // Auto-preenche contato ao selecionar deal com contact_id
                            if (deal?.contact_id && deal.contact_id !== contactId) {
                              setContactId(deal.contact_id);
                              // v1.9.0: cliente será re-derivado pelo contato do negócio
                              setCustomerId(null);
                              setVehicles([]);
                            }
                          }}
                          placeholder="Selecionar negócio..."
                        />
                      </div>
                      {canCreateDeal && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon-sm"
                          title="Novo negócio"
                          aria-label="Novo negócio"
                          onClick={() => setCreateDealOpen(true)}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* v1.4.0: Veículos vinculados (múltiplos)
                    v1.11.0: só aparece com o módulo Veículos habilitado no tenant */}
                {vehiclesModuleEnabled && (
                <div className="grid grid-cols-2 gap-3">

                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <Car className="w-3 h-3" />
                      Veículos
                    </Label>
                    {/* v1.10.0 — seletor + atalho de criação inline */}
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                    <VehicleSelector
                      value={null}
                      onSelect={() => {}}
                      /* v1.9.0: escopa a listagem ao cliente/contato selecionado */
                      scopeCustomerId={vehiclesScopeToCustomer ? vehicleScopeCustomerId : null}
                      scopeContactId={vehiclesScopeToCustomer ? contactId : null}
                      onSelectVehicle={(vehicle) => {
                        // Adiciona à lista sem duplicar
                        if (!vehicle || vehicles.some((v) => v.id === vehicle.id)) return;
                        const label =
                          [vehicle.plate, vehicle.brand_name, vehicle.model_name]
                            .filter(Boolean)
                            .join(' · ') || 'Veículo';
                        setVehicles((prev) => [...prev, { id: vehicle.id, label }]);
                      }}
                      placeholder="Adicionar veículo..."
                    />
                      </div>
                      {canCreateVehicle && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon-sm"
                          title="Novo veículo"
                          aria-label="Novo veículo"
                          onClick={() => setCreateVehicleOpen(true)}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>

                    {/* Chips dos veículos selecionados */}
                    {vehicles.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {vehicles.map((v) => (
                          <span
                            key={v.id}
                            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px]"
                          >
                            <Car className="w-3 h-3 text-muted-foreground" />
                            {v.label}
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                setVehicles((prev) => prev.filter((item) => item.id !== v.id))
                              }
                              aria-label={`Remover ${v.label}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                )}



                {/* Tarefa Pai - ocupa metade da linha */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      <ListTree className="w-3 h-3" />
                      Tarefa Pai
                    </Label>
                    {/* Campo desabilitado quando criando subtarefa (defaultParentTaskId definido) */}
                    <ParentTaskSelector
                      value={parentTaskId}
                      onSelect={setParentTaskId}
                      onTaskData={(task) => setParentDueDate(task?.due_date || null)}
                      excludeTaskId={editTaskId || undefined}
                      placeholder="Selecionar tarefa pai..."
                      disabled={!!defaultParentTaskId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Footer premium — botões de ação */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50 bg-muted/20 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={(!isModuleTask && !title.trim()) || !categoryId || !phaseId || isPending || (!isEditing && !creationOptions.hasAnyCreatablePhase)}
            className="min-w-[140px] gap-2"
          >
            {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {!isPending && <ClipboardList className="w-3.5 h-3.5" />}
            {getSubmitLabel()}
          </Button>
        </div>
      </DialogContent>

      {/* =============================================================== */}
      {/* v1.10.0 — Modais de criação inline dos relacionamentos          */}
      {/* Reaproveitam os mesmos formulários das páginas; ao salvar, o    */}
      {/* registro criado é selecionado automaticamente no campo.         */}
      {/* =============================================================== */}
      {createContactOpen && currentTenant?.id && (
        <CreateContactDialog
          open={createContactOpen}
          onOpenChange={setCreateContactOpen}
          tenantId={currentTenant.id}
          onSuccess={(created) => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            if (created?.id) {
              setContactId(created.id);
              setCustomerId(created.customer_id ?? null);
              setVehicles([]);
            }
            setCreateContactOpen(false);
          }}
        />
      )}

      {createDealOpen && (
        <DealFormDialog
          open={createDealOpen}
          onOpenChange={setCreateDealOpen}
          /* Seleção inline na tarefa: sem modal de sucesso */
          showSuccessDialog={false}
          defaultContactId={contactId || undefined}
          onSuccess={(newDeal) => {
            queryClient.invalidateQueries({ queryKey: ['crm-deals'] });
            if (newDeal?.id) setDealId(newDeal.id);
            setCreateDealOpen(false);
          }}
        />
      )}

      {createVehicleOpen && (
        <VehicleFormDialog
          open={createVehicleOpen}
          onOpenChange={setCreateVehicleOpen}
          /* Vincula ao cliente/contato já escolhido; sem eles permite escolher o dono */
          scope={{ customerId: vehicleScopeCustomerId, contactId }}
          allowOwnerSelection={!vehicleScopeCustomerId && !contactId}
          onSaved={(vehicleId) => {
            queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
            // Adiciona à lista de veículos da tarefa (rótulo definitivo vem no reload)
            setVehicles((prev) =>
              prev.some((v) => v.id === vehicleId)
                ? prev
                : [...prev, { id: vehicleId, label: 'Veículo cadastrado' }],
            );
            setCreateVehicleOpen(false);
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * TaskFormDialog (wrapper público) — v1.13.0
 *
 * Mantém a mesma API do componente anterior. Quando `allowTypeSelection` está
 * ligada (criação de subtarefa ou atalhos de criação rápida), guarda o tipo
 * escolhido e envolve o formulário em <TaskModuleProvider>, fazendo todos os
 * hooks derivados (useTaskCreationOptions / useTaskCategories / useTaskPhases /
 * useTaskClassifications) recarregarem no módulo selecionado — sem tocar em RPCs.
 *
 * RELEASE: 2026-08-22 v1.13.0 - Opções do seletor filtradas por flag do tenant e
 *          permissão de criação (useAvailableTaskTypes). Com um único tipo
 *          disponível o seletor é ocultado e o modal já abre nesse tipo.
 */
export function TaskFormDialog(props: TaskFormDialogProps) {
  const { allowTypeSelection = false, editTaskId, open } = props;
  // Módulo herdado (rota ou provider acima na árvore) = valor inicial do seletor
  const inheritedModule = useTaskModule();
  // v1.13.0 — tipos que o usuário realmente pode criar
  const { types: availableTypes } = useAvailableTaskTypes();

  /** Tipo inicial: herdado quando permitido; caso contrário o primeiro disponível */
  const resolveInitialType = (): TaskModuleType => {
    if (availableTypes.length === 0) return inheritedModule.type;
    return availableTypes.includes(inheritedModule.type)
      ? inheritedModule.type
      : availableTypes[0];
  };

  const [selectedType, setSelectedType] = useState<TaskModuleType>(resolveInitialType);

  // Seletor só faz sentido na criação E quando há mais de uma opção permitida
  const selectorEnabled = allowTypeSelection && !editTaskId;
  const showSelector = selectorEnabled && availableTypes.length > 1;

  // A cada abertura, volta ao tipo inicial (evita "lembrar" a escolha anterior)
  useEffect(() => {
    if (open) setSelectedType(resolveInitialType());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, availableTypes.join('|'), inheritedModule.type]);

  if (!selectorEnabled) {
    return <TaskFormDialogInner {...props} />;
  }

  return (
    <TaskModuleProvider type={selectedType}>
      <TaskFormDialogInner
        {...props}
        typeSelector={
          showSelector
            ? { value: selectedType, onChange: setSelectedType, options: availableTypes }
            : undefined
        }
      />
    </TaskModuleProvider>
  );
}


